/** Modules and obstacles on the live roof: corners in uv, in 3D and in plan, placement context and conflicts. */
import type { S } from "../api/client";
import type { MessageKey } from "../i18n/keys";
import { planToUv, uvToXyz, type Vec2, type Vec3 } from "./geo.ts";
import type { ObstacleItem, PanelItem } from "./model.ts";
import { faceAt, faceArea, usableAreas, type LiveFace, type LiveRoof } from "./roof.ts";
import { conflictsOf, footprint, gridFor, type ConflictKind, type FaceContext, type Grid, type ModuleSize, type Orientation } from "./snap.ts";

export interface PanelGeom {
  face: LiveFace | null;
  uv: Vec2[];
  xyz: Vec3[];
  plan: Vec2[];
}

export function panelGeometry(p: PanelItem, roof: LiveRoof, module: ModuleSize): PanelGeom {
  const face = roof.faces.find((f) => f.id === p.face_id) ?? null;
  const uv = footprint(p.center, p.orientation, p.rotation, module);
  if (!face) return { face, uv, xyz: [], plan: [] };
  const xyz = uv.map((c) => uvToXyz(face.frame, c));
  return { face, uv, xyz, plan: xyz.map((c) => [c[0], c[1]] as Vec2) };
}

export function toFaceUv(face: LiveFace, p: Vec2): Vec2 {
  return planToUv(face.frame, face.normal, face.d, p);
}

/** Obstacles that touch a face, as outlines in its uv frame. */
export function obstaclesOnFace(face: LiveFace, roof: LiveRoof, obstacles: ObstacleItem[]): Vec2[][] {
  return obstacles
    .filter((o) => o.polygon.some((p) => faceAt(roof, p)?.id === face.id) || centroidFace(o, roof) === face.id)
    .map((o) => o.polygon.map((p) => toFaceUv(face, p)));
}

function centroidFace(o: ObstacleItem, roof: LiveRoof): string | undefined {
  const c: Vec2 = [o.polygon.reduce((s, p) => s + p[0], 0) / o.polygon.length, o.polygon.reduce((s, p) => s + p[1], 0) / o.polygon.length];
  return faceAt(roof, c)?.id;
}

export function faceContext(
  face: LiveFace,
  roof: LiveRoof,
  panels: PanelItem[],
  obstacles: ObstacleItem[],
  setbacks: S["SetbacksOutput"] | null | undefined,
  module: ModuleSize,
  exclude: Set<string> = new Set(),
): FaceContext {
  return {
    areas: usableAreas(face, setbacks),
    others: panels
      .filter((p) => p.face_id === face.id && !exclude.has(p.key))
      .map((p) => footprint(p.center, p.orientation, p.rotation, module)),
    obstacles: obstaclesOnFace(face, roof, obstacles),
  };
}

export function faceGrid(
  face: LiveFace,
  ctx: FaceContext,
  panels: PanelItem[],
  orientation: Orientation,
  module: ModuleSize,
  gap: number,
  exclude: Set<string> = new Set(),
): Grid {
  const placed = panels
    .filter((p) => p.face_id === face.id && !exclude.has(p.key))
    .map((p) => ({ center: p.center, orientation: p.orientation, rotationDeg: p.rotation }));
  return gridFor(placed, ctx.areas, orientation, module, gap);
}

/** Conflicts of every module (by key), as free placement shows them.
 *
 * Each module's footprint is made once, and only modules whose boxes meet are tested for overlap (a grid of cells a
 * module wide): a 500-module drag re-checks in a few milliseconds instead of comparing every pair. */
export function localConflicts(
  panels: PanelItem[],
  roof: LiveRoof,
  obstacles: ObstacleItem[],
  setbacks: S["SetbacksOutput"] | null | undefined,
  module: ModuleSize,
): Record<string, ConflictKind[]> {
  const result: Record<string, ConflictKind[]> = {};
  const faces = new Map(roof.faces.map((f) => [f.id, f]));
  type Item = { key: string; poly: Vec2[]; box: [number, number, number, number] };
  const byFace = new Map<string, Item[]>();
  for (const p of panels) {
    if (!faces.has(p.face_id)) {
      result[p.key] = ["outside_face"];
      continue;
    }
    const poly = footprint(p.center, p.orientation, p.rotation, module);
    const xs = poly.map((q) => q[0]), ys = poly.map((q) => q[1]);
    const list = byFace.get(p.face_id) ?? [];
    list.push({ key: p.key, poly, box: [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)] });
    byFace.set(p.face_id, list);
  }
  const cell = Math.max(0.5, Math.hypot(module.width_m, module.height_m));
  for (const [faceId, items] of byFace) {
    const face = faces.get(faceId)!;
    const ctx = faceContext(face, roof, [], obstacles, setbacks, module);
    const area = faceArea(face);
    const grid = new Map<string, Item[]>();
    const cellsOf = (b: Item["box"], add?: Item) => {
      const out: Item[][] = [];
      for (let i = Math.floor(b[0] / cell); i <= Math.floor(b[2] / cell); i++)
        for (let j = Math.floor(b[1] / cell); j <= Math.floor(b[3] / cell); j++) {
          const k = `${i},${j}`;
          let c = grid.get(k);
          if (!c && add) grid.set(k, (c = []));
          if (c) {
            if (add) c.push(add);
            else out.push(c);
          }
        }
      return out;
    };
    for (const it of items) cellsOf(it.box, it);
    for (const it of items) {
      const near = new Set<Item>();
      for (const c of cellsOf(it.box)) for (const o of c) if (o !== it && o.key !== it.key && o.box[0] <= it.box[2] && it.box[0] <= o.box[2] && o.box[1] <= it.box[3] && it.box[1] <= o.box[3]) near.add(o);
      const kinds = conflictsOf(it.poly, area, { ...ctx, others: [...near].map((o) => o.poly) });
      if (kinds.length) result[it.key] = kinds;
    }
  }
  return result;
}

/** Why a panel is misplaced, as catalog keys (resolve with t()). */
export const CONFLICT_LABEL_KEYS: Record<ConflictKind | "no_face", MessageKey> = {
  outside_face: "editor.conflict.outsideFace",
  setback: "editor.conflict.setback",
  obstacle: "editor.conflict.obstacle",
  overlap: "editor.conflict.overlap",
  no_face: "editor.conflict.noFace",
};
